// ============================================================================
// FormBindingService — единый механизм привязки форм
// ============================================================================
// Все компоненты используют этот сервис для проверки привязок.
// Никакого дублирования логики.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('FormBindingService');

interface FormBinding {
  formId: string;
  formName: string;
  formType: string;
  parentType: string;
  parentCode: string;
  layout: string;
  formModule: string;
  isMain?: boolean;
}

// Кэш привязок (sessionStorage — живёт до закрытия вкладки).
// Ключ ОБЯЗАН включать formType — иначе форма списка «подсовывается» форме объекта.
const bindingCache = new Map<string, FormBinding | null>();

function cacheKey(objectType: string, objectName: string, formType: string): string {
  return `${objectType.toLowerCase()}.${objectName.toLowerCase()}.${formType.toLowerCase()}`;
}

export const formBindingService = {
  // Найти привязанную форму для объекта
  async findBinding(objectType: string, objectName: string, formType: string = 'object'): Promise<FormBinding | null> {
    const key = cacheKey(objectType, objectName, formType);

    // Проверяем кэш
    if (bindingCache.has(key)) {
      return bindingCache.get(key) || null;
    }

    try {
      const sdk = getSDK();
      const result = await sdk.metadata.listForms({});
      const allForms = (result as any)?.items || [];
      
      const objectTypeLower = objectType.toLowerCase();
      const objectNameLower = objectName.toLowerCase();

      // Рендерится ТОЛЬКО ОСНОВНАЯ форма (is_main=true) — явный выбор в свойствах
      // объекта. Нет основной — null → АВТОФОРМА (кастомные без пометки не
      // «подсовываются», как было с «первой найденной»).
      const found = allForms.find((f: any) => {
        const fType = (f.parentType || f.parent_type || '').toLowerCase();
        const fName = (f.parentCode || f.parent_code || '').toLowerCase();
        const fFormType = (f.formType || f.form_type || '').toLowerCase();
        if (fType !== objectTypeLower || fName !== objectNameLower || fFormType !== formType) return false;
        return f.isMain === true || f.isMain === 1;
      });

      const binding: FormBinding | null = found ? {
        formId: found.id,
        formName: found.name,
        formType: found.formType || found.form_type || 'object',
        parentType: found.parentType || found.parent_type || '',
        parentCode: found.parentCode || found.parent_code || '',
        layout: found.layoutJson || found.layout || '',
        formModule: found.formModule || found.form_module || '',
        isMain: true,
      } : null;

      // Сохраняем в кэш ТОЛЬКО найденную форму. null НЕ кэшируем: «формы нет» —
      // временное состояние (сервер мог чиниться: is_main не приходил, тип был
      // скрыт ФО и т.п.). Закэшированный null «отравлял» вкладку до перезагрузки:
      // после исправления на сервере форма не находилась → автоформа.
      if (binding) {
        bindingCache.set(key, binding);
      }

      return binding;
    } catch (e) {
      log.error('findBinding failed', { objectType, objectName, error: e });
      return null;
    }
  },

  // Очистить кэш (все formType для объекта или весь кэш)
  invalidate(objectType?: string, objectName?: string): void {
    if (objectType && objectName) {
      const prefix = `${objectType.toLowerCase()}.${objectName.toLowerCase()}.`;
      for (const key of [...bindingCache.keys()]) {
        if (key.startsWith(prefix)) bindingCache.delete(key);
      }
    } else {
      bindingCache.clear();
    }
  },

  // Найти ВСЕ привязки для объекта
  async listBindings(objectType: string, objectName: string): Promise<FormBinding[]> {
    try {
      const sdk = getSDK();
      const result = await sdk.metadata.listForms({});
      const allForms = (result as any)?.items || [];
      
      const objectTypeLower = objectType.toLowerCase();
      const objectNameLower = objectName.toLowerCase();
      
      return allForms
        .filter((f: any) => {
          const fType = (f.parentType || f.parent_type || '').toLowerCase();
          const fName = (f.parentCode || f.parent_code || '').toLowerCase();
          return fType === objectTypeLower && fName === objectNameLower;
        })
        .map((f: any) => ({
          formId: f.id,
          formName: f.name,
          formType: f.formType || f.form_type || 'object',
          parentType: f.parentType || f.parent_type || '',
          parentCode: f.parentCode || f.parent_code || '',
          layout: f.layoutJson || f.layout || '',
          formModule: f.formModule || f.form_module || '',
          isMain: f.isMain === true || f.isMain === 1,
        }));
    } catch (e) {
      log.error('listBindings failed', { objectType, objectName, error: e });
      return [];
    }
  },
};
